/*
 * Shared data layer for the Salah Times sections.
 *
 * Every third-party call (Aladhan, Overpass/OpenStreetMap, BigDataCloud) and
 * the app-proxy city lookup goes through here so results are cached in
 * localStorage and repeat visits make as few network requests as possible:
 *
 * - Prayer times: one Aladhan *calendar* request per city per month serves
 *   today's times plus the weekly and monthly PDFs.
 * - Nearby mosques: cached per city for 30 days.
 * - Reverse geocoding and resolved city URLs: cached per ~1 km area.
 * - Automated clients (crawlers, headless browsers) skip third-party APIs.
 */
(function () {
  'use strict';

  if (window.SalahData) return;

  const DAY_MS = 24 * 60 * 60 * 1000;
  const PREFIX = 'salah-cache:v1:';
  const LEGACY_PREFIX = 'salah-times_';

  const CONFIG = {
    calendarBaseUrl: 'https://api.aladhan.com/v1/calendar',
    overpassUrl: 'https://overpass-api.de/api/interpreter',
    reverseGeocodeUrl: 'https://api.bigdatacloud.net/data/reverse-geocode-client',
    resolveCityUrl: '/apps/salah/resolve-city',

    // Calendar data for a given month/location/method never changes, so it is
    // kept until the month is well over.
    ttl: {
      calendar: 40 * DAY_MS,
      mosques: 30 * DAY_MS,
      mosquesEmpty: 7 * DAY_MS,
      geocode: 30 * DAY_MS,
      resolvedCity: 7 * DAY_MS
    },

    mosqueSearchRadii: [5000, 15000],
    timingKeys: ['Fajr', 'Sunrise', 'Dhuhr', 'Asr', 'Maghrib', 'Isha']
  };

  const BOT_PATTERN =
    /bot|crawl|spider|slurp|headless|lighthouse|pagespeed|phantom|puppeteer|playwright|selenium|preview|facebookexternalhit|embedly|scrape|curl|wget|python|axios|node-fetch/i;

  const memoryCache = new Map();
  const inflight = new Map();
  let storageAvailable = null;
  let purgedThisPage = false;

  function pad(value) {
    return String(value).padStart(2, '0');
  }

  function getApiDate(date) {
    return [pad(date.getDate()), pad(date.getMonth() + 1), date.getFullYear()].join('-');
  }

  function addDays(date, amount) {
    const result = new Date(date.getTime());
    result.setDate(result.getDate() + amount);
    return result;
  }

  function roundCoord(value, digits) {
    return Number(value).toFixed(digits);
  }

  function isLikelyBot() {
    try {
      if (navigator.webdriver) return true;
      return BOT_PATTERN.test(navigator.userAgent || '');
    } catch (error) {
      return false;
    }
  }

  function hasStorage() {
    if (storageAvailable !== null) return storageAvailable;

    try {
      const probe = `${PREFIX}probe`;
      localStorage.setItem(probe, '1');
      localStorage.removeItem(probe);
      storageAvailable = true;
    } catch (error) {
      storageAvailable = false;
    }

    return storageAvailable;
  }

  function purgeStorage(onlyExpired) {
    if (!hasStorage()) return;

    const now = Date.now();
    const keys = [];

    try {
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (key && (key.indexOf(PREFIX) === 0 || key.indexOf(LEGACY_PREFIX) === 0)) {
          keys.push(key);
        }
      }

      keys.forEach(function (key) {
        if (!onlyExpired || key.indexOf(LEGACY_PREFIX) === 0) {
          localStorage.removeItem(key);
          return;
        }

        try {
          const entry = JSON.parse(localStorage.getItem(key));
          if (!entry || !entry.e || entry.e <= now) localStorage.removeItem(key);
        } catch (error) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {}
  }

  function readCache(key) {
    const now = Date.now();
    const memoryEntry = memoryCache.get(key);

    if (memoryEntry && memoryEntry.e > now) return memoryEntry.v;

    if (!hasStorage()) return null;

    try {
      const raw = localStorage.getItem(PREFIX + key);
      if (!raw) return null;

      const entry = JSON.parse(raw);

      if (!entry || !entry.e || entry.e <= now) {
        localStorage.removeItem(PREFIX + key);
        return null;
      }

      memoryCache.set(key, entry);
      return entry.v;
    } catch (error) {
      return null;
    }
  }

  function writeCache(key, value, ttl) {
    const entry = { e: Date.now() + ttl, v: value };

    memoryCache.set(key, entry);

    if (!hasStorage()) return;

    if (!purgedThisPage) {
      purgedThisPage = true;
      purgeStorage(true);
    }

    const serialized = JSON.stringify(entry);

    try {
      localStorage.setItem(PREFIX + key, serialized);
    } catch (error) {
      // Quota exceeded: drop all Salah cache entries and retry once.
      purgeStorage(false);

      try {
        localStorage.setItem(PREFIX + key, serialized);
      } catch (retryError) {}
    }
  }

  /*
   * Returns the cached value for `key`, or runs `loader` once (even if several
   * callers ask at the same time) and caches its result.
   */
  async function cached(key, loader, ttlFor) {
    const hit = readCache(key);
    if (hit !== null) return { value: hit, source: 'cache' };

    if (!inflight.has(key)) {
      inflight.set(
        key,
        loader().finally(function () {
          inflight.delete(key);
        })
      );
    }

    const value = await inflight.get(key);
    const ttl = typeof ttlFor === 'function' ? ttlFor(value) : ttlFor;

    if (ttl > 0) writeCache(key, value, ttl);

    return { value: value, source: 'api' };
  }

  function trimCalendarDay(day) {
    const gregorian = (day && day.date && day.date.gregorian) || {};
    const timings = {};

    CONFIG.timingKeys.forEach(function (key) {
      if (day.timings && day.timings[key]) timings[key] = day.timings[key];
    });

    return {
      date: {
        gregorian: {
          date: gregorian.date || '',
          weekday: { en: (gregorian.weekday && gregorian.weekday.en) || '' }
        }
      },
      timings: timings
    };
  }

  async function requestCalendarMonth(opts, year, month) {
    const url =
      `${CONFIG.calendarBaseUrl}/${year}/${month}` +
      `?latitude=${encodeURIComponent(opts.latitude)}` +
      `&longitude=${encodeURIComponent(opts.longitude)}` +
      `&method=${encodeURIComponent(opts.method)}` +
      `&school=${encodeURIComponent(opts.school)}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: opts.signal
    });

    if (!response.ok) {
      throw new Error(`Prayer calendar HTTP error: ${response.status}`);
    }

    const result = await response.json();

    if (!result || result.code !== 200 || !Array.isArray(result.data)) {
      throw new Error('Invalid Prayer calendar response.');
    }

    return result.data.map(trimCalendarDay);
  }

  /*
   * opts: { latitude, longitude, method, school, signal }
   * Resolves to { days, source } where days is the trimmed month calendar.
   */
  async function getCalendarMonth(opts, year, month) {
    const key = [
      'cal',
      year,
      pad(month),
      roundCoord(opts.latitude, 4),
      roundCoord(opts.longitude, 4),
      opts.method,
      opts.school
    ].join(':');

    const result = await cached(
      key,
      function () {
        return requestCalendarMonth(opts, year, month);
      },
      CONFIG.ttl.calendar
    );

    return { days: result.value, source: result.source };
  }

  /*
   * Resolves to { data, source } where data has the same shape as the
   * Aladhan /timings response: { timings, date: { gregorian: { weekday } } }.
   */
  async function getPrayerDay(opts, date) {
    const month = await getCalendarMonth(opts, date.getFullYear(), date.getMonth() + 1);
    const apiDate = getApiDate(date);

    const day = month.days.find(function (item) {
      return item.date.gregorian.date === apiDate;
    });

    if (!day || !day.timings.Fajr) {
      throw new Error(`No prayer times found for ${apiDate}.`);
    }

    return { data: day, source: month.source };
  }

  async function getDayRange(opts, startDate, dayCount) {
    const months = new Map();

    for (let i = 0; i < dayCount; i += 1) {
      const day = addDays(startDate, i);
      const key = `${day.getFullYear()}-${day.getMonth() + 1}`;

      if (!months.has(key)) {
        months.set(key, { year: day.getFullYear(), month: day.getMonth() + 1 });
      }
    }

    const monthResults = await Promise.all(
      Array.from(months.values()).map(function (entry) {
        return getCalendarMonth(opts, entry.year, entry.month);
      })
    );

    const byDate = new Map();

    monthResults.forEach(function (result) {
      result.days.forEach(function (day) {
        byDate.set(day.date.gregorian.date, day);
      });
    });

    const rangeDays = [];

    for (let i = 0; i < dayCount; i += 1) {
      const match = byDate.get(getApiDate(addDays(startDate, i)));
      if (match) rangeDays.push(match);
    }

    return rangeDays;
  }

  function parseMosqueElement(element) {
    const tags = element.tags || {};

    return {
      name: tags.name || tags['name:en'] || 'Mosque',
      location:
        tags['addr:suburb'] ||
        tags['addr:neighbourhood'] ||
        tags['addr:street'] ||
        tags['addr:city'] ||
        ''
    };
  }

  async function requestNearbyMosques(latitude, longitude, signal) {
    let anySucceeded = false;

    for (const radius of CONFIG.mosqueSearchRadii) {
      const query =
        `[out:json][timeout:10];` +
        `node["amenity"="place_of_worship"]["religion"="muslim"](around:${radius},${latitude},${longitude});` +
        `out body 6;`;

      try {
        const response = await fetch(CONFIG.overpassUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: `data=${encodeURIComponent(query)}`,
          signal: signal
        });

        if (!response.ok) continue;

        const result = await response.json();
        const elements = (result && result.elements) || [];

        anySucceeded = true;

        if (elements.length) {
          return elements.slice(0, 6).map(parseMosqueElement);
        }
      } catch (error) {
        if (error && error.name === 'AbortError') throw error;
        console.warn('Nearby mosque lookup failed:', error);
      }
    }

    // Only a genuine "nothing found" is worth caching, not an outage.
    if (!anySucceeded) throw new Error('Nearby mosque lookup unavailable.');

    return [];
  }

  async function getNearbyMosques(latitude, longitude, signal) {
    const key = ['mosques', roundCoord(latitude, 3), roundCoord(longitude, 3)].join(':');

    const result = await cached(
      key,
      function () {
        return requestNearbyMosques(latitude, longitude, signal);
      },
      function (mosques) {
        return mosques.length ? CONFIG.ttl.mosques : CONFIG.ttl.mosquesEmpty;
      }
    );

    return result.value;
  }

  async function requestReverseGeocode(latitude, longitude, signal) {
    const url =
      `${CONFIG.reverseGeocodeUrl}` +
      `?latitude=${encodeURIComponent(latitude)}` +
      `&longitude=${encodeURIComponent(longitude)}` +
      `&localityLanguage=en`;

    const response = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: signal
    });

    if (!response.ok) {
      throw new Error(`Reverse geocoding HTTP error: ${response.status}`);
    }

    const result = await response.json();

    return {
      city: result.city || result.locality || result.principalSubdivision || 'Current Location',
      country: result.countryName || '',
      countryCode: result.countryCode || ''
    };
  }

  // Cached per ~1 km grid square, which is well inside any city boundary.
  async function reverseGeocode(latitude, longitude, signal) {
    const key = ['geo', roundCoord(latitude, 2), roundCoord(longitude, 2)].join(':');

    const result = await cached(
      key,
      function () {
        return requestReverseGeocode(latitude, longitude, signal);
      },
      CONFIG.ttl.geocode
    );

    return result.value;
  }

  /*
   * payload: { city, country, countryCode, latitude, longitude, timezone }
   * Resolves to the app proxy's { url, city, country } response. Cached per
   * city + country so repeat "use my location" clicks skip the app server.
   */
  async function resolveCity(payload, signal) {
    const key = [
      'city',
      String(payload.countryCode || '').toLowerCase(),
      String(payload.city || '').trim().toLowerCase()
    ].join(':');

    const result = await cached(
      key,
      async function () {
        const response = await fetch(CONFIG.resolveCityUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
          signal: signal
        });

        if (!response.ok) {
          throw new Error(`Resolve city HTTP error: ${response.status}`);
        }

        const data = await response.json();

        if (!data || !data.url) {
          throw new Error('Invalid resolve city response.');
        }

        return data;
      },
      // Fallback names mean geocoding failed, so don't pin them to one URL.
      payload.countryCode ? CONFIG.ttl.resolvedCity : 0
    );

    return result.value;
  }

  window.SalahData = {
    isLikelyBot: isLikelyBot,
    getPrayerDay: getPrayerDay,
    getCalendarMonth: function (opts, year, month) {
      return getCalendarMonth(opts, year, month).then(function (result) {
        return result.days;
      });
    },
    getDayRange: getDayRange,
    getNearbyMosques: getNearbyMosques,
    reverseGeocode: reverseGeocode,
    resolveCity: resolveCity
  };
})();
